import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import analyticsService from "../AnalyticsService";
import { useSelector } from "react-redux";

const pageNameMap = {
  "/register": "Registration Page",
  "/email-verified": "Post Registration Notification Page",
  "/activate": "Activate Page",
  "/login": "Login Page",
  "/forgot": "Forgot Password Page",
  "/financial-health-score": "Analyst Dashboard",
  "/ewiChatBot/ewi": "Insights Page",
  "/drilldown/graph": "Cluster Page",
  "/drilldown/list/": "View Details Page",
  "/bank/health-analysis/report/": "Dashboard",
  "/cohort-management": "Cohort Management",
  "/history": "History",
  "/campaign-management": "Campaign Management",
  "/user-management": "User Management",
  "/pages/faq": "Support",
};

export const usePageTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastTrackedContextKey = useRef(null);
  const currentDashboard = useSelector(
    (state) => state?.dashboardReducer?.currentDashboard,
  );
  const selectedCard = useSelector(
    (state) => state?.dashboardReducer?.selectedCard,
  );

  const currentPath = `${location?.pathname}${location?.search || ""}`;
  const currentDashboardTitle =
    currentDashboard?.CONFIG?.pageHeader?.pageTitle || null;
  const selectedCardName =
    currentDashboard?.KEY === 2
      ? selectedCard?.commercial_title
      : selectedCard?.retail_title;
  const trackingContextKey = `${currentPath}|${navigationType}|${currentDashboardTitle ?? ""}|${selectedCardName ?? ""}|${location?.pathname ?? ""}`;

  useEffect(() => {
    if (lastTrackedContextKey.current === trackingContextKey) {
      return undefined;
    }

    let timeoutId = null;
    let loadHandler = null;

    const runTracking = () => {
      lastTrackedContextKey.current = trackingContextKey;

      const pageName =
        Object.entries(pageNameMap).find(([route]) =>
          currentPath.includes(route),
        )?.[1] || null;

      analyticsService.trackPageView({
        page_name: pageName || document?.title,
        page_path: currentPath,
        navigation_type: navigationType,
        dashboard: currentDashboardTitle,
        navigated_from_card: selectedCardName || null,
      });
    };

    const scheduleTracking = () => {
      if (document.readyState === "complete") {
        timeoutId = window.setTimeout(runTracking, 150);
        return;
      }

      loadHandler = () => {
        timeoutId = window.setTimeout(runTracking, 150);
      };

      window.addEventListener("load", loadHandler, { once: true });
    };

    scheduleTracking();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (loadHandler) {
        window.removeEventListener("load", loadHandler);
      }
    };
  }, [
    trackingContextKey,
    currentPath,
    navigationType,
    currentDashboardTitle,
    selectedCardName,
  ]);
};
